import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  InputAdornment,
  Slider,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  Calculate as CalculateIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  Pinterest as PinterestIcon,
  X as XIcon,
  Language as WebIcon,
  LinkedIn as LinkedInIcon,
  ExpandMore as ExpandMoreIcon,
  Movie as ReelIcon,
  Image as PostIcon,
  AutoStories as StoryIcon,
  PlayCircle as VideoIcon,
  Article as ArticleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import api from '../../../../services/api';
import {
  colors,
  operationalBrandColors
} from '../../../../design-system/tokens';

// Icone customizado para TikTok
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// Icone customizado para Facebook
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...props}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const ViewRateManagement = () => {
  const theme = useTheme();
  const [configs, setConfigs] = useState([]);
  const [groupedConfigs, setGroupedConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    viewRate: 10,
    description: ''
  });

  // Test states
  const [testFollowers, setTestFollowers] = useState(1000000);
  const [testResult, setTestResult] = useState(null);

  // YouTube e Twitter removidos: sempre temos views
  const platforms = ['Instagram', 'Facebook', 'TikTok', 'LinkedIn', 'Pinterest'];

  const contentTypeLabels = {
    story: 'Story',
    reel: 'Reel',
    post: 'Post',
    pin: 'Pin',
    video: 'Video',
    short: 'Short',
    tweet: 'Tweet',
    article: 'Artigo'
  };

  const getPlatformIcon = (platform) => {
    const iconProps = { sx: { fontSize: '1.5rem' } };
    const icons = {
      Instagram: <InstagramIcon {...iconProps} sx={{ ...iconProps.sx, color: '#E4405F' }} />,
      YouTube: <YouTubeIcon {...iconProps} sx={{ ...iconProps.sx, color: operationalBrandColors.youtube }} />,
      Twitter: <XIcon {...iconProps} />,
      TikTok: <TikTokIcon style={{ fontSize: '1.5rem' }} />,
      Facebook: <FacebookIcon style={{ fontSize: '1.5rem', color: '#1877F2' }} />,
      LinkedIn: <LinkedInIcon {...iconProps} sx={{ ...iconProps.sx, color: '#0A66C2' }} />,
      Pinterest: <PinterestIcon {...iconProps} sx={{ ...iconProps.sx, color: '#E60023' }} />
    };
    return icons[platform] || <WebIcon {...iconProps} />;
  };

  const getContentTypeIcon = (contentType) => {
    const iconProps = { sx: { fontSize: '1.2rem', mr: 0.5 } };
    const icons = {
      story: <StoryIcon {...iconProps} />,
      reel: <ReelIcon {...iconProps} />,
      post: <PostIcon {...iconProps} />,
      pin: <PostIcon {...iconProps} />,
      video: <VideoIcon {...iconProps} />,
      short: <ReelIcon {...iconProps} />,
      tweet: <XIcon {...iconProps} />,
      article: <ArticleIcon {...iconProps} />
    };
    return icons[contentType] || <WebIcon {...iconProps} />;
  };

  const getContentTypeColor = (contentType) => {
    const colors = {
      story: '#9c27b0',
      reel: '#e91e63',
      post: '#2196f3',
      pin: '#E60023',
      video: colors.error.main,
      short: '#ff9800',
      tweet: '#1da1f2',
      article: '#4caf50'
    };
    return colors[contentType] || '#757575';
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString('pt-BR');
  };

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/view-rate');
      const data = response.data.data || [];
      setConfigs(data);

      // Agrupar por plataforma
      const grouped = {};
      data.forEach(config => {
        if (!grouped[config.platform]) {
          grouped[config.platform] = [];
        }
        grouped[config.platform].push(config);
      });
      setGroupedConfigs(grouped);
      setError('');
    } catch (error) {
      console.error('Erro ao buscar configuracoes:', error);
      setError('Erro ao carregar configuracoes: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleEditClick = (config) => {
    setSelectedConfig(config);
    setFormData({
      viewRate: (config.viewRate * 100),
      description: config.description || ''
    });
    setEditDialogOpen(true);
  };

  const handleTestClick = (config) => {
    setSelectedConfig(config);
    setTestFollowers(1000000);
    setTestResult(null);
    setTestDialogOpen(true);
  };

  const handleUpdateConfig = async () => {
    try {
      const viewRateDecimal = formData.viewRate / 100;

      await api.put(`/view-rate/${selectedConfig.platform}/${selectedConfig.contentType}`, {
        viewRate: viewRateDecimal,
        description: formData.description
      });

      setSuccess(`Taxa de ${selectedConfig.contentType} do ${selectedConfig.platform} atualizada para ${formData.viewRate}%!`);
      setEditDialogOpen(false);
      fetchConfigs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao atualizar:', error);
      setError('Erro ao atualizar: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCalculateTest = async () => {
    try {
      const response = await api.post('/view-rate/calculate', {
        platform: selectedConfig.platform,
        contentType: selectedConfig.contentType,
        followers: testFollowers
      });

      setTestResult(response.data.data);
    } catch (error) {
      console.error('Erro ao calcular:', error);
      setError('Erro ao calcular: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleInitializeDefaults = async () => {
    try {
      setLoading(true);
      await api.post('/view-rate/init');
      setSuccess('Configuracoes padrao inicializadas com sucesso!');
      fetchConfigs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao inicializar padroes:', error);
      setError('Erro ao inicializar padroes: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (loading && configs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <VisibilityIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1.5 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Taxa de Visualização Estimada
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure a porcentagem de seguidores usada para estimar visualizações
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchConfigs}
            disabled={loading}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={handleInitializeDefaults}
            disabled={loading}
          >
            Inicializar Padroes
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Info Box */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Como funciona:</strong> Estas taxas sao usadas APENAS como fallback quando nao conseguimos extrair metricas reais.
          <br /><br />
          <strong>Quando e usado:</strong>
          <br />- <strong>Instagram Stories:</strong> Sempre (nao tem metricas publicas)
          <br />- <strong>Instagram Reels/Posts:</strong> Quando likes estao escondidos para visitantes nao logados
          <br />- <strong>Facebook/LinkedIn:</strong> Quando nao conseguimos extrair metricas
          <br /><br />
          <strong>Quando NAO e usado:</strong>
          <br />- <strong>YouTube:</strong> Sempre temos views via API oficial
          <br />- <strong>Twitter/X:</strong> Sempre temos views
          <br />- Qualquer conteudo onde conseguimos extrair likes ou views reais
          <br /><br />
          <strong>Exemplo:</strong> Perfil com 1M seguidores + taxa de 15% = 150.000 visualizacoes estimadas
        </Typography>
      </Alert>

      {/* Tabs por plataforma */}
      <Card sx={{ backgroundColor: theme.palette.background.paper, border: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Todas" />
          {platforms.map((platform, index) => (
            <Tab
              key={platform}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {getPlatformIcon(platform)}
                  <span>{platform}</span>
                </Box>
              }
            />
          ))}
        </Tabs>
      </Card>

      {/* Conteudo baseado na tab selecionada */}
      {activeTab === 0 ? (
        // Todas as plataformas
        <Grid container spacing={3}>
          {platforms.map((platform) => (
            <Grid item xs={12} key={platform}>
              <Accordion
                defaultExpanded
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  border: 1, borderColor: 'divider'
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '2rem' }}>
                      {getPlatformIcon(platform)}
                    </Box>
                    <Typography variant="h6">{platform}</Typography>
                    <Chip
                      label={`${groupedConfigs[platform]?.length || 0} tipos`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {(groupedConfigs[platform] || []).map((config) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={`${config.platform}-${config.contentType}`}>
                        <Card
                          variant="outlined"
                          sx={{
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              boxShadow: theme.shadows[4],
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Box display="flex" alignItems="center">
                                {getContentTypeIcon(config.contentType)}
                                <Typography variant="subtitle1" fontWeight="medium">
                                  {contentTypeLabels[config.contentType] || config.contentType}
                                </Typography>
                              </Box>
                              <Chip
                                label={config.isActive ? 'Ativo' : 'Inativo'}
                                size="small"
                                color={config.isActive ? 'success' : 'default'}
                                variant="outlined"
                              />
                            </Box>

                            <Typography
                              variant="h3"
                              sx={{ color: getContentTypeColor(config.contentType), mb: 1 }}
                            >
                              {(config.viewRate * 100).toFixed(1)}%
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                              {config.description}
                            </Typography>

                            <Box display="flex" gap={1}>
                              <Tooltip title="Editar taxa">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditClick(config)}
                                  color="primary"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Testar calculo">
                                <IconButton
                                  size="small"
                                  onClick={() => handleTestClick(config)}
                                  color="secondary"
                                >
                                  <CalculateIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      ) : (
        // Plataforma especifica
        <Card
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: 1, borderColor: 'divider'
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '2.5rem' }}>
                {getPlatformIcon(platforms[activeTab - 1])}
              </Box>
              <Box>
                <Typography variant="h5">{platforms[activeTab - 1]}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure as taxas para cada tipo de conteudo
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              {(groupedConfigs[platforms[activeTab - 1]] || []).map((config) => (
                <Grid item xs={12} sm={6} md={4} key={`${config.platform}-${config.contentType}`}>
                  <Card
                    variant="outlined"
                    sx={{
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: theme.shadows[4],
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Box display="flex" alignItems="center">
                          {getContentTypeIcon(config.contentType)}
                          <Typography variant="h6">
                            {contentTypeLabels[config.contentType] || config.contentType}
                          </Typography>
                        </Box>
                        <Chip
                          label={config.isActive ? 'Ativo' : 'Inativo'}
                          size="small"
                          color={config.isActive ? 'success' : 'default'}
                        />
                      </Box>

                      <Typography
                        variant="h2"
                        sx={{ color: getContentTypeColor(config.contentType), mb: 1, textAlign: 'center' }}
                      >
                        {(config.viewRate * 100).toFixed(1)}%
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                        {config.description}
                      </Typography>

                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="caption">
                          1M seguidores = <strong>{formatNumber(1000000 * config.viewRate)}</strong> views
                        </Typography>
                      </Alert>

                      <Box display="flex" gap={1} justifyContent="center">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditClick(config)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CalculateIcon />}
                          onClick={() => handleTestClick(config)}
                          color="secondary"
                        >
                          Testar
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tabela resumida */}
      <Card
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: 1, borderColor: 'divider',
          mt: 3
        }}
      >
        <CardContent>
          <Typography variant="h6" color="text.primary" gutterBottom>
            Resumo de Todas as Taxas
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Plataforma</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Taxa</TableCell>
                  <TableCell align="right">1M Seg.</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Acoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map((config) => (
                  <TableRow key={`${config.platform}-${config.contentType}`} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getPlatformIcon(config.platform)}
                        </Box>
                        <Typography variant="body2">{config.platform}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getContentTypeIcon(config.contentType)}
                        <Typography variant="body2">
                          {contentTypeLabels[config.contentType] || config.contentType}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ color: getContentTypeColor(config.contentType) }}
                      >
                        {(config.viewRate * 100).toFixed(1)}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatNumber(1000000 * config.viewRate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={config.isActive ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={config.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleEditClick(config)} color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Testar">
                          <IconButton size="small" onClick={() => handleTestClick(config)} color="secondary">
                            <CalculateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog de edicao */}
      <PlatformDialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <PlatformDialogTitle 
          title="Editar Taxa de Visualização"
          subtitle={`${selectedConfig?.platform} — ${contentTypeLabels[selectedConfig?.contentType] || selectedConfig?.contentType}`}
          icon={selectedConfig ? getPlatformIcon(selectedConfig.platform) : <SettingsIcon />}
          onClose={() => setEditDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Ajuste Fino da Taxa: <Box component="span" sx={{ color: 'primary.main' }}>{formData.viewRate.toFixed(1)}%</Box>
              </Typography>
              <Slider
                value={formData.viewRate}
                onChange={(e, newValue) => setFormData({ ...formData, viewRate: newValue })}
                min={0.1}
                max={50}
                step={0.1}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value.toFixed(1)}%`}
                sx={{ mt: 1, mb: 3 }}
              />
            </Box>

            <TextField
              fullWidth
              label="Taxa Nominal (%)"
              size="small"
              type="number"
              value={formData.viewRate}
              onChange={(e) => setFormData({ ...formData, viewRate: parseFloat(e.target.value) || 0 })}
              InputProps={{
                endAdornment: <InputAdornment position="end" sx={{ fontWeight: 700 }}>%</InputAdornment>,
                inputProps: { min: 0.1, max: 100, step: 0.1 },
                sx: { borderRadius: 2 }
              }}
            />

            <TextField
              fullWidth
              label="Descrição / Justificativa"
              size="small"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              placeholder="Ex: Baseado em benchmark do setor..."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Simulação:</strong> Em um perfil de <strong>1M seguidores</strong>, esta taxa gerará <strong>{formatNumber(1000000 * (formData.viewRate / 100))}</strong> views estimadas.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button
            onClick={handleUpdateConfig}
            variant="contained"
            disabled={formData.viewRate <= 0}
            sx={{ fontWeight: 700, borderRadius: 2, px: 4 }}
          >
            Salvar Alterações
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Dialog de teste */}
      <PlatformDialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <PlatformDialogTitle 
          title="Testar Cálculo de Visualização"
          subtitle={`${selectedConfig?.platform} — ${contentTypeLabels[selectedConfig?.contentType] || selectedConfig?.contentType}`}
          icon={<CalculateIcon />}
          onClose={() => setTestDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label="Volume de Seguidores"
              size="small"
              type="number"
              value={testFollowers}
              onChange={(e) => setTestFollowers(parseInt(e.target.value) || 0)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              helperText="Insira o número de seguidores para simular o alcance."
            />

            <Button
              variant="contained"
              onClick={handleCalculateTest}
              startIcon={<CalculateIcon />}
              fullWidth
              sx={{ py: 1.2, fontWeight: 700, borderRadius: 2 }}
            >
              Simular Resultado
            </Button>

            {testResult && (
              <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'action.hover', borderStyle: 'dashed' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                   <VisibilityIcon fontSize="small" color="primary" /> Resultado do Cálculo
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Seguidores Base:</Typography>
                    <Typography variant="h6" fontWeight={700}>{formatNumber(testResult.followers)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Taxa Aplicada:</Typography>
                    <Typography variant="h6" fontWeight={700} color="primary.main">{testResult.viewRatePercent}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Estimativa de Visualizações:</Typography>
                      <Typography variant="h3" color="primary.main" fontWeight={800}>
                        {formatNumber(testResult.estimatedViews)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTestDialogOpen(false)} variant="outlined" fullWidth sx={{ fontWeight: 600, borderRadius: 2 }}>
            Fechar Simulador
          </Button>
        </DialogActions>
      </PlatformDialog>
    </Box>
  );
};

export default ViewRateManagement;
